import { isNull } from 'lodash';
import APIEndpoints from '../utils/apiEndpoints';
import AbstractService from './abstractService';

// Singleton instance
let instance = null;

const _SERVICE_UNAVAILABLE_ = new Error({ message : 'Service Unavailable' });

export default class JobService extends AbstractService {
	static setup(baseURL, options) {
		instance = new JobService(baseURL, options);
	}

	static getInstance() {
		return instance;
	}

	constructor(baseURL, options) {
		super(baseURL, options);

		this.endpoints = {
			jobEvents     : APIEndpoints.jobEvents,
			jobs          : APIEndpoints.jobs,
			officers      : APIEndpoints.officers,
			stats         : APIEndpoints.stats,
			counts        : APIEndpoints.counts,
			checkIns      : APIEndpoints.checkIns,
			jobEventTypes : APIEndpoints.jobEventTypes,
			vehicleTypes  : APIEndpoints.vehicleTypes,
			dressTypes    : APIEndpoints.dressTypes,
		};
	}

	static getAll(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getAll(...params);
	}

	static getAllByOfficerId(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getAllByOfficerId(...params);
	}

	static getById(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getById(...params);
	}

	static add(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._add(...params);
	}

	static edit(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._edit(...params);
	}

	static getStats(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getStats(...params);
	}

	static getCounts(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getCounts(...params);
	}

	static getCheckIns(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getCheckIns(...params);
	}

	static delete(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._delete(...params);
	}

	static restore(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._restore(...params);
	}

	static deleteMultipleJobs(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._deleteMultipleJobs(...params);
	}

	static getJobEventTypes() {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getJobEventTypes();
	}

	static getVehicleTypes(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getVehicleTypes(...params);
	}

	static getDressTypes(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getDressTypes(...params);
	}

	static postJob(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._postJob(...params);
	}

	static postMultipleJobs(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._postMultipleJobs(...params);
	}

	static getApplicantsByPositionId(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getApplicantsByPositionId(...params);
	}

	static getAssignedOfficersByPositionId(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getAssignedOfficersByPositionId(...params);
	}

	static getJobHistoryById(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getJobHistoryById(...params);
	}

	static saveClientJobRequest(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._saveClientJobRequest(...params);
	}

	_getAll({ getCancelToken, ...rest }) {
		return this.get({
			endpoint    : this.endpoints.jobEvents,
			queryParams : rest,
			getCancelToken,
		});
	}

	_getAllByOfficerId({ officerId, ...rest }) {
		return this.get({
			endpoint    : `${this.endpoints.officers}/${officerId}/${this.endpoints.jobEvents}`,
			queryParams : rest,
		});
	}

	_getById(id) {
		return this.get({ endpoint : `${this.endpoints.jobEvents}/${id}` });
	}

	_add(data) {
		return this.post({
			endpoint : this.endpoints.jobEvents,
			data,
		});
	}

	_edit(data) {
		return this.put({
			endpoint    : `${this.endpoints.jobEvents}`,
			queryParams : {
				updateAllUpcoming : data.UpdateAllUpcoming,
			},
			data,
		});
	}

	_getStats({
		dateRange,
		timeZone,
	}) {
		return this.get({
			endpoint    : `${this.endpoints.jobEvents}/${this.endpoints.stats}/${dateRange}`,
			queryParams : { timeZone },
		});
	}

	_getCounts({
		dateRange,
		clientId,
		locationId,
		includeInactive,
		unStaffed,
		deleted,
		recurrenceId,
		startDate,
		endDate,
		prePay,
	}) {
		return this.get({
			endpoint    : `${this.endpoints.jobEvents}/${this.endpoints.counts}`,
			queryParams : {
				dateRange,
				clientId,
				locationId,
				includeInactive,
				unStaffed,
				deleted,
				recurrenceId,
				startDate,
				endDate,
				prePay,
			},
		});
	}

	_getCheckIns(id, userId) {
		return this.get({
			endpoint    : `${this.endpoints.checkIns}/${id}`,
			queryParams : { userId },
		});
	}

	_delete(queryParams) {
		return this.del({
			endpoint : this.endpoints.jobEvents,
			queryParams,
		});
	}

	_restore(queryParams) {
		return this.put({
			endpoint : `${this.endpoints.jobEvents}/restore`,
			queryParams,
		});
	}

	_deleteMultipleJobs(data) {
		return this.del({
			endpoint : this.endpoints.jobEvents,
			data,
		});
	}

	_getJobEventTypes() {
		return this.get({
			endpoint : this.endpoints.jobEventTypes,
		});
	}

	_getVehicleTypes() {
		return this.get({
			endpoint : this.endpoints.vehicleTypes,
		});
	}

	_getDressTypes() {
		return this.get({
			endpoint : this.endpoints.dressTypes,
		});
	}

	_postJob(queryParams, data) {
		return this.put({
			endpoint : `${this.endpoints.jobEvents}/publish`,
			queryParams,
			data,
		});
	}

	_postMultipleJobs(queryParams, data) {
		return this.put({
			endpoint : `${this.endpoints.jobEvents}/publish/multiple`,
			queryParams,
			data,
		});
	}

	_getApplicantsByPositionId(id) {
		return this.get({
			endpoint : `${this.endpoints.jobs}/${id}/applications`,
		});
	}

	_getAssignedOfficersByPositionId(id) {
		return this.get({
			endpoint : `${this.endpoints.jobs}/${id}/assignedOfficers`,
		});
	}

	_getJobHistoryById(id) {
		return this.get({
			endpoint : `${this.endpoints.jobEvents}/${id}/history`,
		});
	}

	_saveClientJobRequest(data) {
		return this.post({
			endpoint : APIEndpoints.clientjobrequest,
			data,
		});
	}
}
